import { Navigate, Route, Routes } from 'react-router';
import type { Capability } from '@reditelji/shared';
import { lazy, Suspense, type ReactNode } from 'react';
import { useEvent } from './api/hooks';
import { useSession } from './api/session';
import { BriefingTokenEntry } from './briefing/TokenEntry';
import { readClock } from './live/clock';
import { MailPage } from './mail/MailPage';
import { MapPage } from './map/MapPage';
import { OverviewPage } from './overview/OverviewPage';
import { PostsPage } from './posts/PostsPage';
import { PredajePage } from './predaje/PredajePage';
import { CoordinatorLayout } from './routes/CoordinatorLayout';
import { LoginPage } from './routes/LoginPage';
import { TimelinePage } from './routes/TimelinePage';
import { SettingsPage } from './settings/SettingsPage';
import { VolunteersPage } from './volunteers/VolunteersPage';

/**
 * The volunteer briefing loads on its own: its fonts, map and stylesheet are
 * nothing a coordinator screen uses, and a marshal on a phone at a trailhead
 * should not download the coordinator app to read their shift.
 */
const BriefingPage = lazy(() => import('./briefing/BriefingPage'));

/**
 * Coordinator screens: Pregled, Časovnica, Predaje, Mesta, Prostovoljci,
 * Zemljevid, Sporočila, Nastavitve; the volunteer briefing lives under /b (own
 * magic-link auth, separate visual world).
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/b/:token" element={<BriefingTokenEntry />} />
      <Route
        path="/b"
        element={
          <Suspense fallback={null}>
            <BriefingPage />
          </Suspense>
        }
      />
      <Route element={<CoordinatorLayout />}>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/casovnica" element={<TimelinePage />} />
        <Route path="/prostovoljci" element={<VolunteersPage />} />
        <Route path="/zemljevid" element={<MapPage />} />
        <Route path="/pregled" element={<OverviewPage />} />
        <Route path="/predaje" element={<PredajePage />} />
        <Route path="/mesta" element={<PostsPage />} />
        <Route
          path="/sporocila"
          element={
            <RequireCapability any={['comms:write']}>
              <MailPage />
            </RequireCapability>
          }
        />
        <Route
          path="/nastavitve"
          element={
            <RequireCapability any={['settings:write', 'access:manage']}>
              <SettingsPage />
            </RequireCapability>
          }
        />
      </Route>
    </Routes>
  );
}

/** Mount rule: land on the timeline on the event day, else Pregled. */
function RootRedirect() {
  const event = useEvent();
  if (event.isPending) return null;
  const today = readClock().isoDate;
  const isEventDay = event.data?.days.some((d) => d.date === today) ?? false;
  return <Navigate to={isEventDay ? '/casovnica' : '/pregled'} replace />;
}

/** A screen this login has nothing to do on (SPEC §1.1L) sends it to Pregled. */
function RequireCapability({ any, children }: { any: Capability[]; children: ReactNode }) {
  const { can } = useSession();
  return any.some(can) ? children : <Navigate to="/pregled" replace />;
}
